import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { useBuInstance, useUpdateBuInstance } from "@/hooks/use-bu-instances";
import { BuInstanceForm } from "./BuInstanceForm";

interface BuInstanceEditDialogProps {
  id: string | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Edit dialog — fetches fresh data via `useBuInstance(id, { enabled: isOpen })`
 * per `.claude/rules/frontend.md`'s Dialogs rule, wraps the same
 * `BuInstanceForm` used by create. The fetched record never carries a
 * password (see the Fleet Relay build plan's Phase 2 credential-handling
 * decision), so the form's password field always starts blank here too.
 */
export function BuInstanceEditDialog({ id, onOpenChange }: BuInstanceEditDialogProps) {
  const { label } = useBuInstanceLabel();
  const isOpen = id !== null;
  const { data, isLoading } = useBuInstance(id ?? "", { enabled: isOpen });
  const updateBuInstance = useUpdateBuInstance(id ?? "");

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {label}</DialogTitle>
          <DialogDescription>Update this Coach Manager connection.</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <BuInstanceForm
            key={data.id}
            isEdit
            submitLabel="Save"
            isSaving={updateBuInstance.isPending}
            defaultValues={{
              name: data.name,
              endpointUrl: data.endpointUrl,
              username: data.username,
              companyId: data.companyId ?? undefined,
              active: data.active,
            }}
            onCancel={() => onOpenChange(false)}
            onSubmit={(result) =>
              updateBuInstance.mutate(result, { onSuccess: () => onOpenChange(false) })
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
